import { api, ApiError } from './client';

// ===== Auth =====

export interface AdminIdentity {
  authenticated: boolean;
}

export async function fetchAdminIdentity(signal?: AbortSignal): Promise<AdminIdentity> {
  try {
    const res = await api.get<AdminIdentity>('/admin/api/me', { signal });
    return { authenticated: !!res.authenticated };
  } catch (err) {
    if (err instanceof ApiError && err.isUnauthorized) {
      return { authenticated: false };
    }
    throw err;
  }
}

export function adminLogin(password: string): Promise<{ ok: true }> {
  return api.post('/admin/login', { password });
}

export function adminLogout(): Promise<{ ok: true }> {
  return api.post('/admin/logout');
}

// ===== Invite =====

/** Liefert den dauerhaften, mehrfach nutzbaren Trip-Invite-Token. Backend prüft
 *  Trip-Session (nicht Admin-Cookie!) — Admin muss daher im selben Browser auch
 *  eine Trip-Session haben, sonst 401 / 403. */
export function createInviteToken(slug: string): Promise<{ ok: true; token: string; expiresAt?: number }> {
  return api.post<{ ok: true; token: string; expiresAt?: number }>(`/trips/${encodeURIComponent(slug)}/api/invite`);
}

// ===== Trips =====

export interface AdminTripListItem {
  slug: string;
  header: string;
  subtitle?: string;
  dateBadge?: string;
  dateStart?: string;
  dateEnd?: string;
  startTime?: string;
  updatedAt?: string;
}

export interface AdminTripBlock {
  type: 'heading' | 'text';
  text: string;
}

export interface AdminMalleTip {
  text: string;
}

export interface AdminTrip {
  slug: string;
  header: string;
  subtitle?: string;
  dateBadge?: string;
  dateStart?: string;
  dateEnd?: string;
  startTime?: string;
  password?: string;
  blocks?: AdminTripBlock[];
  malleTips?: AdminMalleTip[];
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminAct {
  id: string;
  type?: 'act' | 'meetup';
  date?: string;
  time?: string;
  endTime?: string;
  artist?: string;
  area?: string;
  address?: string;
  lat?: number;
  lng?: number;
  entry?: string;
  price?: string;
  note?: string;
  location?: string;
  title?: string;
}

export function fetchAdminTrips(signal?: AbortSignal): Promise<{ trips: AdminTripListItem[] }> {
  return api.get('/admin/api/trips', { signal });
}

export function fetchAdminTrip(slug: string, signal?: AbortSignal): Promise<{ trip: AdminTrip; acts: AdminAct[] }> {
  return api.get(`/admin/api/trips/${encodeURIComponent(slug)}`, { signal });
}

export function putAdminTrip(slug: string, trip: AdminTrip): Promise<{ trip: AdminTrip }> {
  return api.put(`/admin/api/trips/${encodeURIComponent(slug)}`, trip);
}

export function deleteAdminTrip(slug: string): Promise<{ ok: true }> {
  return api.delete(`/admin/api/trips/${encodeURIComponent(slug)}`);
}

export function putAdminActs(slug: string, acts: AdminAct[]): Promise<{ acts: AdminAct[] }> {
  return api.put(`/admin/api/trips/${encodeURIComponent(slug)}/acts`, { acts });
}

/** Schickt Screenshot(s) an die KI und bekommt Act-Vorschläge zurück (nicht gespeichert). */
export function parseAdminActsFromImage(slug: string, files: Blob[]): Promise<{ acts: AdminAct[] }> {
  const form = new FormData();
  for (const f of files) form.append('image', f);
  return api.post(`/admin/api/trips/${encodeURIComponent(slug)}/acts/parse-image`, form);
}

// ===== Posts =====

export interface AdminPost {
  id: string;
  name: string;
  token: string;
  note: string;
  lat?: number;
  lng?: number;
  createdAt: string;
  photoKey: string;
  hasLegacyImage: boolean;
  reactions: Record<string, string[]>;
}

export function fetchAdminPosts(slug: string, signal?: AbortSignal): Promise<{ posts: AdminPost[] }> {
  return api.get(`/admin/api/trips/${encodeURIComponent(slug)}/posts`, { signal });
}

export function deleteAdminPost(slug: string, postId: string): Promise<{ ok: true }> {
  return api.delete(`/admin/api/trips/${encodeURIComponent(slug)}/posts/${encodeURIComponent(postId)}`);
}

// ===== Users =====

export interface AdminUser {
  username?: string;
  name: string;
  token: string;
  canInvite?: boolean;
  hidden?: boolean;
  disabled?: boolean;
  lastSeenAt?: string;
  createdAt?: string;
  isOrphan?: boolean;
}

export function fetchAdminUsers(slug: string, signal?: AbortSignal): Promise<{ users: AdminUser[] }> {
  return api.get(`/admin/api/trips/${encodeURIComponent(slug)}/users`, { signal });
}

export function patchAdminUser(
  slug: string,
  username: string,
  patch: { canInvite?: boolean; hidden?: boolean; disabled?: boolean },
): Promise<{ ok: true; user: AdminUser }> {
  return api.patch(`/admin/api/trips/${encodeURIComponent(slug)}/users/${encodeURIComponent(username)}`, patch);
}

export function deleteAdminUser(slug: string, username: string, wipeData: boolean): Promise<{ ok: true }> {
  return api.delete(
    `/admin/api/trips/${encodeURIComponent(slug)}/users/${encodeURIComponent(username)}`,
    { wipeData },
  );
}

/** Stats-Aufstellung nach Bulk-Wipe — wie viele KV-Keys pro Kategorie gelöscht. */
export interface BulkWipeStats {
  ok: true;
  stats: {
    accounts?: number; users?: number; tokenmaps?: number; sessions?: number;
    subs?: number; pending?: number; chatRead?: number; pushRate?: number; liveloc?: number;
  };
  photosDeleted?: number;
}

export function wipeAllAdminUsers(slug: string, wipeData: boolean): Promise<BulkWipeStats> {
  return api.delete<BulkWipeStats>(`/admin/api/trips/${encodeURIComponent(slug)}/users`, { wipeData });
}

export function deleteAdminOrphan(slug: string, token: string, wipeData: boolean): Promise<{ ok: true }> {
  return api.delete(
    `/admin/api/trips/${encodeURIComponent(slug)}/orphans/${encodeURIComponent(token)}`,
    { wipeData },
  );
}
